import React, { useState } from "react";
import Slider from "react-slick";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { getAttachmentLargeUrl } from "../utils/attachmentImages";

const getPrefetchIndices = (activeIndex, total) => {
  if (total <= 0) return new Set();
  if (total === 1) return new Set([0]);

  const prev = (activeIndex - 1 + total) % total;
  const next = (activeIndex + 1) % total;
  return new Set([prev, activeIndex, next]);
};

const CustomPrevArrow = ({ className, style, onClick }) => {
  return (
    <div
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,.5)",
        borderRadius: "50%",
        left: "15px",
        zIndex: 1,
        width: "20px",
        height: "20px",
        cursor: "pointer",
        transition: "all 0.3s ease",
      }}
      onClick={onClick}
    ></div>
  );
};

const CustomNextArrow = ({ className, style, onClick }) => {
  return (
    <div
      className={className}
      style={{
        ...style,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: "rgba(0,0,0,.5)",
        borderRadius: "50%",
        right: "15px",
        zIndex: 1,
        width: "20px",
        height: "20px",
        cursor: "pointer",
        transition: "all 0.3s ease",
      }}
      onClick={onClick}
    ></div>
  );
};

const Carousel = ({ attachments, fallbackSrc }) => {
  const slideCount = attachments?.length ?? 0;

  const [loadedSlideIndices, setLoadedSlideIndices] = useState(() =>
    getPrefetchIndices(0, slideCount)
  );

  const prefetchAround = (index) => {
    setLoadedSlideIndices((prev) => {
      const next = new Set(prev);
      getPrefetchIndices(index, slideCount).forEach((i) => next.add(i));
      return next;
    });
  };

  const hasAttachments = Array.isArray(attachments) && attachments.length > 0;

  if (!hasAttachments) {
    if (!fallbackSrc) return null;
    return (
      <div className="relative w-full h-44 sm:h-56 p-2">
        <img
          src={fallbackSrc}
          alt="Asset"
          className="w-full h-full object-contain rounded-md"
        />
      </div>
    );
  }

  if (attachments.length === 1) {
    return (
      <div className="relative w-full h-44 sm:h-56 p-2">
        <img
          src={getAttachmentLargeUrl(attachments[0])}
          alt="Asset 1"
          className="w-full h-full object-contain rounded-md"
        />
      </div>
    );
  }

  const settings = {
    dots: false,
    infinite: true,
    speed: 200,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    autoplay: false,
    prevArrow: <CustomPrevArrow />,
    nextArrow: <CustomNextArrow />,
    beforeChange: (_current, next) => {
      prefetchAround(next);
    },
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
      {
        breakpoint: 480,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  return (
    <Slider {...settings}>
      {attachments.map((attachment, index) => (
        <div
          key={attachment.id ?? index}
          className="relative w-full h-48 sm:h-56 p-2"
        >
          {loadedSlideIndices.has(index) ? (
            <img
              src={getAttachmentLargeUrl(attachment)}
              alt={`Asset ${index + 1}`}
              className="w-full h-full object-contain rounded-md"
            />
          ) : (
            <div className="h-full w-full rounded-md bg-gray-100" />
          )}
        </div>
      ))}
    </Slider>
  );
};

export default Carousel;
